import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import React, { Suspense } from "react";
import { AuthProvider, useAuth } from "./lib/auth";
import VersionChecker from "./components/VersionChecker";
import MonitoringBridge from "./components/MonitoringBridge";
import AnalyticsBridge from "./components/AnalyticsBridge";
import CeremindBackground from "./components/CeremindBackground";
import AdminRoute from "./components/AdminRoute";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AppShell from "./components/AppShell";
import PublicRSVP from "./pages/PublicRSVP";
import MobileSimulator from "./pages/MobileSimulator";
import PublicWedding from "./pages/PublicWedding";
import PublicGuestUpload from "./pages/PublicGuestUpload";
import CollaboratorPortal from "./pages/collaborator/CollaboratorPortal";
import CollaboratorLogin from "./pages/collaborator/CollaboratorLogin";
import CollaboratorPortalUnavailable from "./pages/collaborator/CollaboratorPortalUnavailable";
import { COLLABORATOR_PORTAL_ENABLED } from "./pages/collaborator/collaboratorAccess";
 
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("./pages/ResetPassword"));
const CollaboratorForgotPassword = React.lazy(() => import("./pages/collaborator/CollaboratorForgotPassword"));
const CollaboratorResetPassword = React.lazy(() => import("./pages/collaborator/CollaboratorResetPassword"));

const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Guests = React.lazy(() => import("./pages/Guests"));
const TablePlan = React.lazy(() => import("./pages/TablePlan/index"));
const Ceremony = React.lazy(() => import("./pages/Ceremony"));
const Invitations = React.lazy(() => import("./pages/Invitations"));
const RSVPAdmin = React.lazy(() => import("./pages/RSVPAdmin"));
const Gifts = React.lazy(() => import("./pages/Gifts"));
const Vendors = React.lazy(() => import("./pages/Vendors"));
const AIChatPage = React.lazy(() => import("./pages/AIChatPage"));
const Settings = React.lazy(() => import("./pages/Settings"));
const Calendar = React.lazy(() => import("./pages/Calendar"));
const Timeline = React.lazy(() => import("./pages/Timeline"));
const Moments = React.lazy(() => import("./pages/Moments"));
const Tasks = React.lazy(() => import("./pages/Tasks"));
const Budget = React.lazy(() => import("./pages/Budget"));
const Chat = React.lazy(() => import("./pages/Chat"));
const Documents = React.lazy(() => import("./pages/Documents"));
const Team = React.lazy(() => import("./pages/Team"));
const GuestMessages = React.lazy(() => import("./pages/GuestMessages"));
const WeddingSite = React.lazy(() => import("./pages/WeddingSite"));
const AdminConsole = React.lazy(() => import("./pages/AdminConsole"));
const CeremonyLyricsAR = React.lazy(() => import("./pages/ar/CeremonyLyricsAR"));
const MixologyTutor = React.lazy(() => import("./pages/ar/MixologyTutor"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-ivory"><div className="font-serif text-2xl">Chargement…</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App relative min-h-screen">
      <AuthProvider>
        <Toaster position="top-right" />
        <VersionChecker />
        <MonitoringBridge />
        <BrowserRouter>
          <AnalyticsBridge />
          <CeremindBackground />
          <Suspense fallback={<div className="flex items-center justify-center h-screen">Chargement...</div>}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/rsvp/:token" element={<PublicRSVP />} />
            <Route path="/w/:slug" element={<PublicWedding />} />
            <Route path="/w/:slug/guest-upload" element={<PublicGuestUpload />} />
            
            <Route path="/ar/ceremony/:slug" element={<CeremonyLyricsAR />} />
            <Route path="/ar/bar/:slug/:drink_id" element={<MixologyTutor />} />

            <Route
              path="/collaborator-login"
              element={COLLABORATOR_PORTAL_ENABLED ? <CollaboratorLogin /> : <CollaboratorPortalUnavailable />}
            />
            <Route path="/collaborator-forgot-password" element={<CollaboratorForgotPassword />} />
            <Route path="/collaborator-reset-password" element={<CollaboratorResetPassword />} />
            <Route
              path="/collaborator-hub"
              element={COLLABORATOR_PORTAL_ENABLED ? <CollaboratorPortal /> : <CollaboratorPortalUnavailable />}
            />

            <Route path="/simulateur" element={<Protected><MobileSimulator /></Protected>} />
            {/* Keep older PWA links working, but always open the real app and
                its live data rather than the former isolated mobile mockup. */}
            <Route path="/app/mobile" element={<Protected><Navigate to="/app" replace /></Protected>} />

            <Route path="/app" element={<Protected><AppShell /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="site" element={<WeddingSite />} />
              <Route path="guests" element={<Guests />} />
              <Route path="tables" element={<TablePlan />} />
              <Route path="ceremony" element={<Ceremony />} />
              <Route path="invitations" element={<Invitations />} />
              <Route path="rsvp" element={<RSVPAdmin />} />
              <Route path="guest-messages" element={<GuestMessages />} />
              <Route path="gifts" element={<Gifts />} />
              <Route path="vendors" element={<Vendors />} />
              <Route path="ai" element={<AIChatPage />} />
              <Route path="settings" element={<Settings />} />
              <Route path="admin" element={<AdminRoute><AdminConsole /></AdminRoute>} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="timeline" element={<Timeline />} />
              <Route path="moments" element={<Moments />} />
              <Route path="tasks" element={<Tasks />} />
              <Route path="budget" element={<Budget />} />
              <Route path="chat" element={<Chat />} />
              <Route path="documents" element={<Documents />} />
              <Route path="team" element={<Team />} />
            </Route>
          </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
